import type { ReadyRosieVideo, Story, StoryPage } from '@tadpoles/common'

import type { LibraryCategory } from './catalog.js'

export type LibraryCatalogFailure = {
    readonly blocking: boolean
    readonly retryable: true
}

export type LibraryCatalogStatus = {
    readonly failure?: LibraryCatalogFailure
    readonly hasUsableCatalog: boolean
    readonly phase: 'idle' | 'loading' | 'available' | 'failure'
}

export type LibraryCatalogState<Item> = LibraryCatalogStatus & {
    readonly items: ReadonlyArray<Readonly<Item>>
}

export type LibraryCatalogs = {
    readonly ebook: LibraryCatalogState<Story>
    readonly video: LibraryCatalogState<ReadyRosieVideo>
}

export type LibraryCatalogStatusUpdate = {
    failure?: LibraryCatalogFailure
    hasUsableCatalog: boolean
    phase: LibraryCatalogStatus['phase']
}

class ReactiveLibraryCatalogState<Item> {
    #failure = $state.raw<LibraryCatalogFailure | undefined>()
    #hasUsableCatalog = $state(false)
    #items = $state.raw<Item[]>([])
    #phase = $state<LibraryCatalogStatus['phase']>('idle')
    public readonly view: LibraryCatalogState<Item>

    public constructor() {
        const getFailure = (): LibraryCatalogFailure | undefined => this.#failure
        const getHasUsableCatalog = (): boolean => this.#hasUsableCatalog
        const getItems = (): Item[] => this.#items
        const getPhase = (): LibraryCatalogStatus['phase'] => this.#phase
        this.view = {
            get failure() {
                return getFailure()
            },
            get hasUsableCatalog() {
                return getHasUsableCatalog()
            },
            get items() {
                return getItems()
            },
            get phase() {
                return getPhase()
            }
        }
    }

    public replace(items: Item[], status: LibraryCatalogStatusUpdate): void {
        this.#items = items
        this.replaceStatus(status)
    }

    public replaceStatus(status: LibraryCatalogStatusUpdate): void {
        this.#failure = status.failure
        this.#hasUsableCatalog = status.hasUsableCatalog
        this.#phase = status.phase
    }

    public reset(): void {
        this.replace([], { failure: undefined, hasUsableCatalog: false, phase: 'idle' })
    }
}

export class ReactiveLibraryState {
    readonly #ebook = new ReactiveLibraryCatalogState<Story>()
    #pagesByStoryId = $state.raw<ReadonlyMap<string, readonly StoryPage[]>>(new Map())
    readonly #video = new ReactiveLibraryCatalogState<ReadyRosieVideo>()
    public readonly catalogs: LibraryCatalogs

    public constructor() {
        this.catalogs = {
            ebook: this.#ebook.view,
            video: this.#video.view
        }
    }

    public get pagesByStoryId(): ReadonlyMap<string, readonly StoryPage[]> {
        return this.#pagesByStoryId
    }

    public replaceStories(
        stories: Story[],
        pagesByStoryId: ReadonlyMap<string, readonly StoryPage[]>,
        status: LibraryCatalogStatusUpdate
    ): void {
        this.#pagesByStoryId = pagesByStoryId
        this.#ebook.replace(stories, status)
    }

    public replaceVideos(videos: ReadyRosieVideo[], status: LibraryCatalogStatusUpdate): void {
        this.#video.replace(videos, status)
    }

    public replaceStatus(category: LibraryCategory, status: LibraryCatalogStatusUpdate): void {
        if (category === 'ebook') {
            this.#ebook.replaceStatus(status)
            return
        }

        this.#video.replaceStatus(status)
    }

    public reset(): void {
        this.#pagesByStoryId = new Map()
        this.#ebook.reset()
        this.#video.reset()
    }
}
